/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, Table, TableProps } from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import UserProfile from './UserProfile';

import { UserProfileData } from './UserProfile';

interface User extends UserProfileData {
  key: number;
}

const UserTable: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        page: currentPage,
        page_size: pageSize,
        ...(searchValue && { search: searchValue }),
      };
      const response = await api.get('/auth/all-user', payload);
      const data: User[] = (response?.users || []).map((item: any, index: number) => ({
        key: (currentPage - 1) * pageSize + index,
        user_id: item.user_id ?? '',
        name: item.username || '-',
        email: item.email || item.username,
        role: item.role || '-',
        access_organizations: item.access_organizations || [],
        not_access_organizations: item.not_access_organizations || [],
      }));
      setUsers(data);
      setTotalCount(response?.total_records ?? data.length);
    } catch {
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, searchValue]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trimStart().toLowerCase();
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setSearchValue('');
      setCurrentPage(1);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setSearchValue(value.trim());
      setCurrentPage(1);
    }, 1000);
  };

  const handleSearchEnter = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchValue(inputValue.trim());
    setCurrentPage(1);
  };

  const handleTableChange = (pagination: any) => {
    const { current, pageSize: newPageSize } = pagination;
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    } else {
      setCurrentPage(current);
    }
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  const columns: TableProps<User>['columns'] = [
    {
      title: 'User',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Details',
      key: 'details',
      width: 120,
      align: 'center',
      render: (_: any, record: User) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <>
      <Flex gap={8} style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 280 }}
          placeholder="Search by email"
          allowClear
          value={inputValue}
          onChange={handleSearchChange}
          onPressEnter={handleSearchEnter}
          suffix={<SearchOutlined />}
        />
      </Flex>
      <Table
        scroll={{ x: 'max-content' }}
        loading={loading}
        columns={columns}
        dataSource={users}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          hideOnSinglePage: false,
          pageSizeOptions: ['10', '20', '50'],
        }}
      />
      <UserProfile open={drawerOpen} user={selectedUser} onClose={() => setDrawerOpen(false)} onUpdate={fetchUsers} />
    </>
  );
};

export default UserTable;
